import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Select } from '@components/core-ui/form-elements/Select';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { JobResponseDto, JobsService } from 'app/API';
import { useNotifyQueryError } from 'app/hooks/useNotifyQueryError';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { jobsKeys } from 'app/queries/jobs/jobsKeys';
import { createFetchAllOrganizationRepositoriesQueryFn } from 'app/queries/repositories/createFetchAllOrganizationRepositoriesQueryFn';
import { repositoriesKeys } from 'app/queries/repositories/repositoriesKeys';
import { useMemo, useState } from 'react';

import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { Content } from './EditJobCaseModal/styles/Content';

type TProps = {
  job: JobResponseDto;
  onCancel: () => void;
  onSuccess: () => void;
};

export const EditJobCaseModal = ({ job, onCancel, onSuccess }: TProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { organization } = useAuthContext();
  const [repositoryId, setRepositoryId] = useState<string>(job?.repositoryId || job?.repository?.id || '');
  const organizationId = organization?.id ?? '';

  const repositoriesQuery = useQuery({
    queryKey: repositoriesKeys.allByOrg(organizationId),
    queryFn: createFetchAllOrganizationRepositoriesQueryFn(call),
    enabled: !!organizationId,
  });
  useNotifyQueryError(repositoriesQuery.error);

  const repositoryList = useMemo(
    () =>
      repositoriesQuery.data
        ? [{ value: '', label: 'No case selected' }].concat(
            repositoriesQuery.data.map((item) => ({ value: item.id, label: item.name })),
          )
        : [],
    [repositoriesQuery.data],
  );

  const updateCaseMutation = useMutation({
    mutationFn: () =>
      call(() => JobsService.updateCase({ jobId: job.id, wsid: job.workspaceId ?? '', requestBody: { repositoryId } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobsKeys.detail(job.id) });
      void queryClient.invalidateQueries({ queryKey: jobsKeys.lists() });
      onSuccess();
    },
    onError: notifyApiCallError,
  });

  return (
    <ModalWrapper width="487px">
      <ModalTitle>Edit Job Case</ModalTitle>
      <Content>
        <Select
          name="repositoryId"
          label="Case"
          value={repositoryId}
          onChange={(_, value) => setRepositoryId(value)}
          options={repositoryList}
          required={false}
        />

        <div className="buttonGroup">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button onClick={() => updateCaseMutation.mutate()} disabled={updateCaseMutation.isPending}>
            Save
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
